import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { apiGet, apiPut } from '../../lib/api.js';
import { Card, Field, SaveButton, isDirty, LoadingPanel } from './_shared.jsx';

const TZ = ['America/Denver', 'America/Los_Angeles', 'America/Phoenix', 'America/Chicago', 'America/New_York', 'America/Anchorage', 'Pacific/Honolulu'];
const PALETTE = ['#7C3AED', '#E040FB', '#3B82F6', '#0A0A0F'];

export default function TenantTab() {
  const [loaded, setLoaded] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiGet('/settings/tenant').then((r) => {
      if (r?.ok) {
        setLoaded(r.tenant);
        setDraft(r.tenant);
      }
    }).catch((e) => toast.error(e.message || 'Failed to load tenant'));
  }, []);

  async function save() {
    const prev = loaded;
    setBusy(true);
    setLoaded(draft);
    try {
      const r = await apiPut('/settings/tenant', {
        name: draft.name,
        tagline: draft.tagline,
        logoUrl: draft.logoUrl,
        faviconUrl: draft.faviconUrl,
        colors: draft.colors,
        sendWindowsTimezone: draft.sendWindowsTimezone,
        brandName: draft.brandName,
      });
      if (!r?.ok) throw new Error('Save failed');
      setLoaded(r.tenant);
      setDraft(r.tenant);
      toast.success('Tenant settings saved');
    } catch (e) {
      setLoaded(prev);
      setDraft(prev);
      toast.error(e.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  }

  if (!draft) return <LoadingPanel label="Loading tenant settings…" />;
  const dirty = isDirty(draft, loaded);

  return (
    <div className="space-y-4">
      <Card
        title="Tenant Profile"
        description="Brand name, logo, and colors used in the navbar, login screen, and outbound emails."
        actions={<SaveButton onClick={save} dirty={dirty} busy={busy} />}
      >
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Brand name">
            <input className="input" value={draft.name || ''}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}/>
          </Field>
          <Field label="Tagline">
            <input className="input" value={draft.tagline || ''}
              onChange={(e) => setDraft({ ...draft, tagline: e.target.value })}/>
          </Field>
          <Field label="SMS Brand Name" hint="Appears at the end of the first text to a new contact, e.g. '…Reply STOP to opt out. — Civix LLC'">
            <input className="input" value={draft.brandName || ''}
              onChange={(e) => setDraft({ ...draft, brandName: e.target.value })}/>
          </Field>
          <Field label="Logo URL" hint="Public HTTPS URL — square, 256×256+ recommended.">
            <input className="input" value={draft.logoUrl || ''}
              onChange={(e) => setDraft({ ...draft, logoUrl: e.target.value })}
              placeholder="https://…"/>
          </Field>
          <Field label="Favicon URL">
            <input className="input" value={draft.faviconUrl || ''}
              onChange={(e) => setDraft({ ...draft, faviconUrl: e.target.value })}
              placeholder="https://…"/>
          </Field>
          <Field label="Default Timezone" hint="Used for compliance windows + scheduled sends.">
            <select className="input"
              value={draft.sendWindowsTimezone || 'America/Denver'}
              onChange={(e) => setDraft({ ...draft, sendWindowsTimezone: e.target.value })}>
              {TZ.map((z) => <option key={z}>{z}</option>)}
            </select>
          </Field>
        </div>
      </Card>

      <Card title="Brand Colors" description="Override the default CIVIX palette. Stick to the 4 brand colors below for visual consistency.">
        <div className="flex flex-wrap items-center gap-3">
          {PALETTE.map((hex) => (
            <button key={hex}
              type="button"
              onClick={() => setDraft({ ...draft, colors: { ...(draft.colors || {}), primary: hex } })}
              className={`w-10 h-10 rounded-lg border-2 transition ${
                draft.colors?.primary?.toUpperCase() === hex
                  ? 'border-white ring-2 ring-white/30'
                  : 'border-white/10'
              }`}
              style={{ background: hex }}
              title={hex}
            />
          ))}
        </div>
        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <Field label="Primary">
            <input className="input font-mono" value={draft.colors?.primary || ''}
              onChange={(e) => setDraft({ ...draft, colors: { ...(draft.colors || {}), primary: e.target.value } })}/>
          </Field>
          <Field label="Accent">
            <input className="input font-mono" value={draft.colors?.accent || ''}
              onChange={(e) => setDraft({ ...draft, colors: { ...(draft.colors || {}), accent: e.target.value } })}/>
          </Field>
          <Field label="Background">
            <input className="input font-mono" value={draft.colors?.bg || ''}
              onChange={(e) => setDraft({ ...draft, colors: { ...(draft.colors || {}), bg: e.target.value } })}/>
          </Field>
        </div>
      </Card>
    </div>
  );
}
